import { useState } from "react";
import { subscriptionService, taskService } from "@miplatform/api";
import { HTTP_STATUSES } from "@miplatform/constants";
import { useComponentState } from "@miplatform/hooks";

export const useOrderTemplateViewModel = ({ orderTemplateId }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [orderTemplate, setOrderTemplate] = useState({});
  const [trades, setTrades] = useState([]);
  const [workOrderTemplateModalOpen, setWorkOrderTemplateModalOpen] =
    useState(false);
  const stateManager = useComponentState();

  const getOrderTemplateDetails = () => {
    stateManager.run(async () => {
      const response = await taskService.orderTemplates.getById({
        id: orderTemplateId,
      });

      if (response.status === HTTP_STATUSES.OK) {
        setOrderTemplate(response.data);
      }
    });
  };

  const getTrades = () => {
    stateManager.run(async () => {
      const response = await subscriptionService.trades.getAll();
      if (response.status === HTTP_STATUSES.OK) {
        const updatedTrades = response.data.map(({ name, id }) => ({
          label: name,
          value: id,
        }));

        setTrades(updatedTrades);
      }
    });
  };

  const getAllAssignmentTemplates = () => {
    return stateManager.run(async () => {
      const response = await taskService.assignmentTemplates.getAll({
        sort: "name asc",
      });

      if (response.status === HTTP_STATUSES.OK) {
        const existingTemplateIds = new Set(
          orderTemplate.assignmentTemplates.map(({ id }) => id)
        );

        return response.data.filter(({ id }) => !existingTemplateIds.has(id));
      }

      return [];
    });
  };

  const handleAddAssignmentTemplate = (data) => {
    stateManager.run(async () => {
      const { status } =
        await taskService.orderTemplateAssignmentTemplateMaps.create({
          data: { orderTemplateId, ...data },
          toastMessageConfig: {
            success: {
              text: `${data.name} was added.`,
            },
          },
        });

      if (status === HTTP_STATUSES.OK) {
        closeModal();
        getOrderTemplateDetails();
      }
    });
  };

  const onDetailsUpdate = (data) => {
    stateManager.run(async () => {
      const { name, modifiedOn } = orderTemplate;

      const { status } = await taskService.orderTemplates.update({
        id: orderTemplateId,
        data: { ...data, modifiedOn },
        toastMessageConfig: {
          success: {
            text: `${name}'s details were updated.`,
          },
        },
      });

      if (status === HTTP_STATUSES.OK) {
        getOrderTemplateDetails();
      }
    });
  };

  const onInputOptionsUpdate = (data) => {
    stateManager.run(async () => {
      const { name, modifiedOn } = orderTemplate;
      const { status } = await taskService.orderTemplates.updateConfigById({
        id: orderTemplateId,
        data: { fields: data },
        modifiedOn,
        toastMessageConfig: {
          success: {
            text: `${name}'s input options were updated.`,
          },
        },
      });

      if (status === HTTP_STATUSES.OK) {
        getOrderTemplateDetails();
      }
    });
  };

  const openModal = () => {
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const openWorkOrderTemplateModal = (value) => {
    setWorkOrderTemplateModalOpen(value);
  };

  return {
    orderTemplate,
    loading: stateManager.isBusy(),
    isModalOpen,
    workOrderTemplateModalOpen,
    openWorkOrderTemplateModal,
    getAllAssignmentTemplates,
    handleAddAssignmentTemplate,
    closeModal,
    openModal,
    getOrderTemplateDetails,
    onDetailsUpdate,
    onInputOptionsUpdate,
    getTrades,
    trades,
  };
};
